/**
 * Offline Sync Service
 *
 * Buffers mutations made while offline in the Dexie `sync_queue` table and
 * flushes them to the server when connectivity returns.
 *
 * The queue is entity-agnostic (`entity_type` / `entity_id` / `operation` /
 * `data`). Today only `entity_type === "visit"` items have a corresponding
 * server endpoint (POST /visits/sync); items for other entities wait in the
 * queue until their sync endpoints exist.
 *
 * Queue item lifecycle:
 *   pending → syncing → done | conflict | failed
 *
 * Logging routes through the PHI-safe `logger` util — primitives only, never
 * payloads, IDs, or server error text.
 *
 * Retention: `done` items are pruned after SYNC_DONE_RETENTION_MS so PHI-
 * bearing payloads don't accumulate forever. `conflict` / `failed` items are
 * kept until the user resolves them (they carry unsynced data).
 */

import {
  API_BASE_URL,
  DEVICE_ID_KEY,
  ROUTES,
  SYNC_DONE_RETENTION_MS,
  SYNC_FETCH_TIMEOUT_MS,
  SYNC_MAX_RETRIES,
} from "../constants";
import { db } from "../db/schema";
import { wrapSyncQueuePayload, unwrapSyncQueuePayload } from "../db/encryption-bridge";
import type {
  EntityType,
  SyncItemStatus,
  SyncOperation,
  SyncQueueOnDisk,
  SyncQueueRecord,
} from "../db/db.types";
import { isClinicScopeStale } from "../utils/active-clinic";
import { logger } from "../utils/logger";
import { apiFetch } from "./api.client";

// ─── Types (re-exported for call sites) ──────────────────────────────────────

export type { EntityType, SyncItemStatus, SyncOperation };

export interface SyncBatchResult {
  synced:    number;
  conflicts: number;
  failed:    number;
}

// Server contract (kept internal — clients speak in SyncOperation).
type ServerAction = "create" | "patch" | "finalize";

interface ServerResult {
  client_id: string;
  success:   boolean;
  conflict:  boolean;
  error?:    string;
  visit_id?: string;
  sync_version?: number;
}

interface ServerResponse {
  synced:    number;
  conflicts: number;
  failed:    number;
  results:   ServerResult[];
}

// ─── Device ID ───────────────────────────────────────────────────────────────

function getDeviceId(): string {
  let id = localStorage.getItem(DEVICE_ID_KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(DEVICE_ID_KEY, id);
  }
  return id;
}

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Add one mutation to the offline queue.
 *
 * @param operation  create | update | delete
 * @param entityType which table the mutation targets
 * @param entityId   PK of the affected record; null for pending creates
 * @param data       payload to apply
 * @param version    local version the mutation was made against (optimistic
 *                   lock; defaults to 1, which is correct for creates)
 */
export async function enqueue(
  operation:  SyncOperation,
  entityType: EntityType,
  entityId:   string | null,
  data:       Record<string, unknown>,
  version:    number = 1,
): Promise<void> {
  // Deduplicate: collapse multiple pending writes for the same entity into one
  // row, replacing the data in-place. Operation merge rules:
  //   "create" is preserved unless the incoming op is "delete" — the entity
  //            hasn't reached the server yet so a subsequent PATCH would 404.
  //   "delete" always wins — it is the terminal operation.
  //   "update" replaces "update" with the latest data snapshot.
  // Only "pending" rows are eligible — an in-flight "syncing" row must not be
  // mutated mid-flight or the server will process a stale snapshot.
  let id: string = crypto.randomUUID();
  let mergedOp = operation;
  if (entityId) {
    const existing = await db.sync_queue
      .where("[entity_type+entity_id]")
      .equals([entityType, entityId])
      .filter(r => r.status === "pending")
      .first();
    if (existing) {
      id = existing.id;
      mergedOp = existing.operation === "create" && operation !== "delete"
               ? "create"
               : operation;
    }
  }

  const envelope = await wrapSyncQueuePayload({
    id,
    entity_type:      entityType,
    entity_id:        entityId,
    operation:        mergedOp,
    data,
    version,
    status:           "pending",
    retry_count:      0,
    device_id:        getDeviceId(),
    client_timestamp: new Date().toISOString(),
  });
  await db.sync_queue.put(envelope as SyncQueueOnDisk);
  registerBackgroundSync();
}

/** Count pending items (used by the UI indicator). */
export function pendingCount(): Promise<number> {
  return db.sync_queue.where("status").equals("pending").count();
}

// ─── Flush ───────────────────────────────────────────────────────────────────

let inFlight: Promise<SyncBatchResult> | null = null;

/**
 * Flush pending items to the server. Concurrent calls are coalesced — the
 * second caller awaits the first.
 *
 * Only `entity_type === "visit"` items are flushed right now (that's the only
 * server sync endpoint). Other entity types remain `pending` for future tasks.
 */
export function flush(): Promise<SyncBatchResult> {
  if (inFlight) return inFlight;
  inFlight = doFlush().finally(() => { inFlight = null; });
  return inFlight;
}

async function doFlush(): Promise<SyncBatchResult> {
  // The shared queue is being drained by the tab that switched clinics.
  if (isClinicScopeStale()) return { synced: 0, conflicts: 0, failed: 0 };

  const pendingEnvelopes = await db.sync_queue
    .where("status").equals("pending")
    .filter(r => r.entity_type === "visit")
    .toArray();

  if (pendingEnvelopes.length === 0) return { synced: 0, conflicts: 0, failed: 0 };

  // Decrypt each envelope's payload back into a plaintext SyncQueueRecord
  // before serialising for the wire. Done OUTSIDE the IDB transaction (Web
  // Crypto promises don't participate in Dexie transaction lifecycle).
  // The cast is required because SyncQueueOnDisk is a non-indexable struct
  // type while unwrapSyncQueuePayload accepts the wider Record shape so it
  // can also tolerate v1 rows during the migration window.
  const pending: SyncQueueRecord[] = await Promise.all(
    pendingEnvelopes.map(env => unwrapSyncQueuePayload(env as unknown as Record<string, unknown>)),
  );

  const deviceId = getDeviceId();

  await db.transaction("rw", db.sync_queue, async () => {
    await Promise.all(pending.map(p => db.sync_queue.update(p.id, { status: "syncing" })));
  });

  const operations = pending.map(toServerOperation);

  try {
    const response = await postBatch(operations, deviceId);
    await applyResults(response.results);

    if (response.conflicts > 0) logger.warn("offline-sync conflicts", { conflicts: response.conflicts });
    if (response.failed    > 0) logger.warn("offline-sync server failures", { failed: response.failed });

    return {
      synced:    response.synced,
      conflicts: response.conflicts,
      failed:    response.failed,
    };
  } catch (err) {
    await requeueOnFailure(pending, err);
    return { synced: 0, conflicts: 0, failed: pending.length };
  }
}

// ─── Client ↔ server translation ─────────────────────────────────────────────

// Map the spec-compliant client op to the server's SyncAction enum. Visit
// finalize is expressed client-side as an `update` carrying {status:"Finalized"};
// we detect it here and route to the finalize code path on the server.
function toServerAction(op: SyncOperation, data: Record<string, unknown>): ServerAction {
  if (op === "create") return "create";
  if (op === "delete") throw new Error("delete not supported by /visits/sync yet");
  return data.status === "Finalized" ? "finalize" : "patch";
}

function toServerOperation(rec: SyncQueueRecord) {
  return {
    client_id:        rec.id,
    action:           toServerAction(rec.operation, rec.data),
    visit_id:         rec.entity_id,
    payload:          rec.data,
    device_id:        rec.device_id,
    client_timestamp: rec.client_timestamp,
  };
}

// ─── HTTP ────────────────────────────────────────────────────────────────────

class HttpError extends Error {
  constructor(readonly status: number) { super(`HTTP ${status}`); }
}

async function postBatch(
  operations: unknown[],
  deviceId:   string,
): Promise<ServerResponse> {
  const res = await apiFetch(`${API_BASE_URL}${ROUTES.VISIT_SYNC}`, {
    method:  "POST",
    headers: { "Content-Type": "application/json" },
    body:   JSON.stringify({ operations, device_id: deviceId }),
    signal: AbortSignal.timeout(SYNC_FETCH_TIMEOUT_MS),
  });

  if (!res.ok) throw new HttpError(res.status);

  const json = await res.json();
  const data = json?.data ?? json;
  if (!data || !Array.isArray(data.results)) {
    throw new Error("Malformed sync response");
  }
  return data as ServerResponse;
}

// ─── Batched IDB updates ─────────────────────────────────────────────────────

async function requeueOnFailure(pending: SyncQueueRecord[], err: unknown): Promise<void> {
  // PHI-safe: never pass err.message (may contain server-side patient refs).
  if (err instanceof HttpError) {
    logger.warn("offline-sync flush HTTP error", { status: err.status, pending: pending.length });
  } else {
    const name = err instanceof Error ? err.name : "Unknown";
    logger.warn("offline-sync flush transport error", { error_name: name, pending: pending.length });
  }

  let exhausted = 0;

  await db.transaction("rw", db.sync_queue, async () => {
    await Promise.all(pending.map(item => {
      const retry_count = item.retry_count + 1;
      if (retry_count >= SYNC_MAX_RETRIES) {
        exhausted += 1;
        return db.sync_queue.update(item.id, {
          status:      "failed",
          retry_count,
          last_error:  "Max retries exceeded",
        });
      }
      return db.sync_queue.update(item.id, { status: "pending", retry_count });
    }));
  });

  if (exhausted > 0) {
    logger.warn("offline-sync items exhausted retries", { exhausted });
  }
}

async function applyResults(results: ServerResult[]): Promise<void> {
  await db.transaction("rw", db.sync_queue, async () => {
    await Promise.all(results.map(r => {
      // Server error strings may contain PHI — stored in IDB (local only, ok)
      // but never forwarded to the logger.
      const patch: Partial<SyncQueueRecord> =
          r.success  ? { status: "done" }
        : r.conflict ? { status: "conflict", last_error: r.error }
        :              { status: "failed",   last_error: r.error };
      return db.sync_queue.update(r.client_id, patch);
    }));
  });
}

// ─── Retention / pruning ─────────────────────────────────────────────────────

/**
 * Delete `done` items older than SYNC_DONE_RETENTION_MS so completed-sync PHI
 * doesn't accumulate on device. Conflict/failed items are kept until the
 * user resolves them. Safe to call often — scans only the indexed status.
 */
export async function pruneDone(): Promise<number> {
  const cutoff = Date.now() - SYNC_DONE_RETENTION_MS;
  const stale  = await db.sync_queue
    .where("status").equals("done")
    .filter(r => new Date(r.client_timestamp).getTime() < cutoff)
    .primaryKeys();
  if (stale.length > 0) {
    await db.sync_queue.bulkDelete(stale);
    logger.warn("offline-sync pruned done items", { count: stale.length });
  }
  return stale.length;
}

// ─── Background Sync registration ────────────────────────────────────────────

/**
 * Register the "sync-queue" Background Sync tag with the service worker.
 * When the device regains connectivity the browser fires the `sync` event in
 * the SW, which messages any open client tabs to call flush(). No-ops
 * gracefully when the SW or SyncManager API is unavailable (iOS < 16, Firefox).
 */
function registerBackgroundSync(): void {
  if (
    typeof navigator === "undefined" ||
    !("serviceWorker" in navigator) ||
    !("SyncManager" in window)
  ) {
    return;
  }
  navigator.serviceWorker.ready
    .then((reg) => (reg as any).sync.register("sync-queue"))
    .catch(() => { /* SyncManager permission denied or SW not controlling */ });
}

// ─── Auto-flush listener ─────────────────────────────────────────────────────

/**
 * Register an online-event listener that auto-flushes on reconnect and once
 * at setup IF items are already queued. Returns a teardown function.
 */
export function setupAutoFlush(onFlush?: (result: SyncBatchResult) => void): () => void {
  const handler = async () => {
    if (!navigator.onLine) return;
    // flush() short-circuits internally if nothing is pending — no extra
    // pendingCount query needed here.
    const result = await flush();
    if (onFlush && (result.synced + result.conflicts + result.failed) > 0) {
      onFlush(result);
    }
  };

  window.addEventListener("online", handler);
  // Fire once at setup to handle the case where the app launched while
  // already online with queued items. Cost when queue is empty: a single
  // indexed `.toArray()` that returns 0 rows (flush short-circuits internally),
  // which subsumes the pendingCount-then-handler optimisation we used to do.
  handler();

  // Opportunistic retention cleanup — runs once per setup, cheap (indexed scan).
  pruneDone().catch(() => { /* non-fatal */ });

  return () => window.removeEventListener("online", handler);
}
